import React, { useContext } from 'react';
import BackButton from '../BackButton';
import {
  ACTIVE_VIEW,
  CustomButtonsContext,
  SYSTEM_BUTTONS_DEFAULT_SCHEMA
} from '../engine/FormBuilderCustomButtonsEngine';
import { SimpleFlexContainer } from '../layouts/ng-layouts/Containers';
import { Container } from './FormBuilderStatusesView';
import styled from 'styled-components/macro';
import useTranslations from '../../hooks/useTranslations';
import Select from '../engine/SelectEngine';
import { BUTTON_TYPE } from '../engine/FormBuilderCustomButtonsEngine';
import InputBase from '../form/InputBase';
import ColorPickeEngine from '../engine/ColorPickerEngine';
import Button from '../Button';
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip
} from '@material-ui/core';
import { getButtonStyle } from '../engine/CustomButtonWizardEngine';
import { FormBuilderContext } from '../engine/FormBuilderEngine';
import Labeler from '../Labeler';
import CharacterCounter from '../../internal-components/CharacterCounter';
import Colors from '../../internal-components/Colors';
import NoStatusesWarningDialog from '../../internal-components/GeneralDialog';
import WizardDirtyWarningDialog from '../../internal-components/GeneralDialog';
import BackspaceIcon from '@material-ui/icons/Backspace';
import _ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import Animation from '../Animation';
import { TextString } from '../Text';

const TitleContainer = styled.div`
  display: flex;
`;
const H2 = styled.h2``;
const H3 = styled.h3`
  color: inherit;
  margin: 0.8rem 0;
`;
const PreviewTitle = styled.h2`
  text-align: center;
  margin-bottom: 0;
`;

const StyleActionsAndPreviewContainer = styled(SimpleFlexContainer)`
  flex-direction: row;
  margin: 2rem 0;
`;

const StyleActionsContainer = styled(SimpleFlexContainer)`
  flex: 1;
  margin-right: 0.8rem;
`;
const PreviewContainer = styled(SimpleFlexContainer)`
  flex: 1;
  border: 1px solid ${Colors.BORDER_GREY};

  border-radius: 20px;
`;

const PreviewBtnHPositioner = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;
const PreviewBtnVPositioner = styled.div`
  justify-content: center;
  display: flex;
  flex-direction: column;
`;

const SaveBtnPositioner = styled.div`
  align-self: end;
`;

const StaticTypeContainer = styled.div`
  margin: 0.8rem 0 1.5rem;
  font-weight: 500;
`;

const DynamicTypeContainer = styled(SimpleFlexContainer)`
  margin-bottom: 2rem;
  align-items: center;
  & > button {
    margin-left: 0.8rem;
  }
`;

const TypeActionContainer = styled.div`
  margin-bottom: 2rem;
`;

const OptionAndWarningContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
`;

const OptionContainer = styled.div`
  flex: 5;
`;

const WarningContainer = styled.div`
  flex: 6;
`;

const Warning = styled.div`
  background-color: #fff2e0;
  border-left: 4px solid #feb44e;
  color: #9c5b11;
  display: ${props => (props.$visible ? 'block' : 'none')};
  font-size: 14px;
  margin-bottom: 0.5rem;
  padding: 0.5rem 1rem;
`;

const ReportProblemOutlinedIcon = styled(_ReportProblemOutlinedIcon)`
  && {
    font-size: 14px;
    margin-right: 0.5rem;
    vertical-align: middle;
  }
`;

const LABEL_MAX_CHAR = 25;

const CustomButtonWizardView = props => {
  const t = useTranslations();
  const { form } = useContext(FormBuilderContext);
  return (
    <>
      <Container>
        <SimpleFlexContainer>
          <TitleContainer>
            <BackButton onClick={props.onBackClicked} />
            <H2>
              {props.isIntegrationButton
                ? t('customButtons.edit_integration')
                : t('customButtons.add')}{' '}
            </H2>
            {/* TODO change title based on if button is new */}
          </TitleContainer>

          <OptionAndWarningContainer>
            {/* This feature should be enabled in the future */}
            <OptionContainer>
              <Labeler label={t('customButtons.wizard.select.type.label')}>
                {/* TODO: phase 2 of custom buttons - make sense of this VVV */}
                {props.isSystemButton ? (
                  <StaticTypeContainer>
                    {t(`customButtons.wizard.select.type.option.${props.type}`)}
                  </StaticTypeContainer>
                ) : props.isIntegrationButton ? (
                  <>
                    <Select
                      value={props.type}
                      onChange={props.onTypeChange}
                      placeholder={t(
                        'customButtons.wizard.select.type.placeholder'
                      )}
                      options={[
                        {
                          value: 'admin',
                          text: t(
                            `customButtons.wizard.select.type.option.admin`
                          ),
                          'data-testid': 'type-admin'
                        },
                        {
                          value: 'eu',
                          text: t(`customButtons.wizard.select.type.option.eu`),
                          'data-testid': 'type-eu'
                        },
                        {
                          value: 'both',
                          text: t(
                            `customButtons.wizard.select.type.option.both`
                          ),
                          'data-testid': 'type-both'
                        }
                      ]}
                      customCSS="max-width: calc(100% - 1rem) !important;"
                    />
                  </>
                ) : (
                  <>
                    <Select
                      value={props.type}
                      onChange={props.onTypeChange}
                      placeholder={t(
                        'customButtons.wizard.select.type.placeholder'
                      )}
                      options={[
                        {
                          value: 'admin',
                          text: t(
                            `customButtons.wizard.select.type.option.admin`
                          ),
                          'data-testid': 'type-admin'
                        },
                        {
                          value: 'eu',
                          text: t(`customButtons.wizard.select.type.option.eu`),
                          'data-testid': 'type-eu'
                        }
                      ]}
                      options={Object.values(BUTTON_TYPE).map(type => ({
                        text: t(
                          `customButtons.wizard.select.type.option.${type}`
                        ),
                        value: type,
                        'data-testid': `custom-button-type-${type}`
                      }))}
                      customCSS="max-width: calc(100% - 1rem) !important;"
                    />
                  </>
                )}
              </Labeler>
            </OptionContainer>
            <WarningContainer>
              <Warning
                $visible={
                  props.type &&
                  (props.type !== 'eu' || props.isUseSaveNotSubmitChecked)
                }
              >
                <ReportProblemOutlinedIcon />
                <TextString content="customButtons.wizard.warning" />
              </Warning>
            </WarningContainer>
          </OptionAndWarningContainer>
          <TypeActionContainer>
            <Animation
              type="collapse"
              in={
                props.type === 'eu' &&
                !props.isSystemButton &&
                !props.isIntegrationButton
              }
            >
              <Labeler
                label={t('customButtons.wizard.operation.label')}
                customCSS="margin-top: 2rem;"
              >
                <FormControl component="fieldset">
                  <RadioGroup
                    value={props.isUseSaveNotSubmitChecked ? 'save' : 'submit'}
                    onChange={e => {
                      const value = e.target.value;
                      props.onUseSaveNotSubmitChecked(value === 'save');
                    }}
                  >
                    <FormControlLabel
                      value="save"
                      control={<Radio color="primary" />}
                      label={t(
                        'customButtons.wizard.operation.radio.option.save'
                      )}
                    />
                    <FormControlLabel
                      value="submit"
                      control={<Radio color="primary" />}
                      label={t(
                        'customButtons.wizard.operation.radio.option.submit'
                      )}
                    />
                  </RadioGroup>
                </FormControl>
              </Labeler>
            </Animation>
          </TypeActionContainer>
          <Labeler label={t('customButtons.wizard.select.status.label')}>
            <DynamicTypeContainer $row>
              <Select
                placeholder={t(
                  'customButtons.wizard.select.status.placeholder'
                )}
                value={props.statuses}
                options={[
                  {
                    text: t('customButtons.wizard.select.status.all'),
                    value: 'all',
                    'data-testid': 'status-all'
                  },
                  ...(props.id === 'REQUEST_MORE_INFO'
                    ? []
                    : [
                        {
                          text: t('customButtons.wizard.select.status.new'),
                          value: 'new',
                          'data-testid': 'status-new'
                        }
                      ]),
                  ...(form.customStatuses || []).map(cs => ({
                    text: `Status: ${cs.label}`,
                    value: cs.internalId,
                    'data-testid': `status-${cs.internalId}`
                  }))
                ]}
                // disabled={
                //   props.id ===
                //   SYSTEM_BUTTONS_DEFAULT_SCHEMA.REQUEST_MORE_INFO.id
                // }
                allowMultiSelect
                onChange={props.onStatusDDChange}
                customCSS="flex: 1; max-width: calc(50% - 0.4rem) !important;"
              />
              <Tooltip
                arrow
                title={t('customButtons.wizard.select.status.select.clear')}
              >
                <IconButton
                  disabled={
                    !props.statuses || !props.statuses.length
                    // ||
                    // props.id ===
                    //   SYSTEM_BUTTONS_DEFAULT_SCHEMA.REQUEST_MORE_INFO.id
                  }
                  size="small"
                  onClick={() => {
                    props.onStatusDDChange([]);
                  }}
                >
                  <BackspaceIcon />
                </IconButton>
              </Tooltip>
            </DynamicTypeContainer>
          </Labeler>
          <Divider />

          <StyleActionsAndPreviewContainer>
            <StyleActionsContainer>
              <Labeler
                label={t('customButtons.wizard.input.label.label')}
                customCSS="margin-bottom: 0.4rem;"
              >
                {/* add char limit */}
                <InputBase
                  value={props.label}
                  onChange={e =>
                    props.dispatch({
                      type: 'update',
                      payload: { label: e.target.value }
                    })
                  }
                  inputProps={{
                    maxLength: LABEL_MAX_CHAR,
                    'data-testid': 'button-label'
                  }}
                />
                <CharacterCounter
                  string={props.label}
                  maxLength={LABEL_MAX_CHAR}
                />
              </Labeler>

              <Labeler label={t('customButtons.wizard.radio.label')}>
                <FormControl component="fieldset">
                  <RadioGroup
                    name="controlled-radio-buttons-group"
                    value={props.variant}
                    onChange={e =>
                      props.dispatch({
                        type: 'update',
                        payload: { variant: e.target.value }
                      })
                    }
                  >
                    <FormControlLabel
                      value="contained"
                      control={<Radio color="primary" />}
                      label={t('customButtons.wizard.radio.option.contained')}
                    />
                    <FormControlLabel
                      value="outlined"
                      control={<Radio color="primary" />}
                      label={t('customButtons.wizard.radio.option.outlined')}
                    />
                  </RadioGroup>
                </FormControl>
              </Labeler>
              <Labeler
                label={t('customButtons.wizard.colorPicker.label')}
                customCSS="margin-bottom: 0.4rem;"
              >
                <ColorPickeEngine
                  defaultColor={props.color}
                  onColorSelected={color => {
                    props.dispatch({ type: 'update', payload: { color } });
                  }}
                />
              </Labeler>
            </StyleActionsContainer>
            <PreviewContainer>
              <PreviewTitle>
                {t('customButtons.wizard.preview.title')}
              </PreviewTitle>
              <PreviewBtnHPositioner>
                <PreviewBtnVPositioner>
                  {(props.label || '').trim() ? (
                    <Button
                      customCSS={getButtonStyle(props.variant, props.color)}
                      variant={props.variant}
                    >
                      {props.label}
                    </Button>
                  ) : (
                    t('customButtons.wizard.preview.emptyLabel')
                  )}
                </PreviewBtnVPositioner>
              </PreviewBtnHPositioner>
            </PreviewContainer>
          </StyleActionsAndPreviewContainer>
          <SaveBtnPositioner>
            <Tooltip arrow title={props.reasonsSaveIsDisabled}>
              <span>
                <Button
                  onClick={() => props.onSave()}
                  disabled={props.reasonsSaveIsDisabled}
                >
                  {t('customButtons.wizard.save.btn')}
                </Button>
              </span>
            </Tooltip>
          </SaveBtnPositioner>
        </SimpleFlexContainer>
      </Container>
      <NoStatusesWarningDialog
        title="dialog.title.areYouSure"
        content="customButtons.wizard.dialog.statuses.text"
        open={props.isNoStatusesDialogOpen}
        handleConfirm={() => {
          props.setIsNoStatusesDialogOpen(false);
          props.onSave(true);
          props.onWizardClosed();
        }}
        handleClose={() => props.setIsNoStatusesDialogOpen(false)}
      />
      <WizardDirtyWarningDialog
        title="dialog.title.areYouSure"
        content="customButtons.wizard.dialog.dirty.text"
        open={props.isWizardDirtyDialogOpen}
        handleConfirm={() => {
          props.setIsWizardDirtyDialogOpen(false);
          props.onWizardClosed();
        }}
        handleClose={() => props.setIsWizardDirtyDialogOpen(false)}
      />
    </>
  );
};

export default CustomButtonWizardView;
